import React, { useMemo } from 'react'
import { Card } from '../../../../../../shared/card/card'
import { useNotificationContext } from '../../../../../../context/notification/context'
import { ButtonPrimaryConfirm, ButtonPrimaryDelay } from '../../../../../../shared/component/button'
import { IconDelete, IconEmail } from '../../../../../../shared/icon/icon'
import moment from 'moment'
import { useApiFormUserInvite } from '../../../../../../api-new/state/content'

const ItemReadOnly = ({ label, value }) => {
  return (
    <React.Fragment>
      <div className="form-group">
        <label className="text-capitalize">
          {label}
        </label>
        <div className="form-control text-nowrap text-truncate">
          {value}
        </div>
      </div>
    </React.Fragment>
  )
}

const labels = { pending: 'Pending', accepted: 'Accepted', revoked: 'Revoked', expired: 'Expired', declined: 'Declined', member: 'Member, invitation open' }

export const inviteStatusLabel = status => labels[status] || ''

const day = value => (value ? moment(value).format('DD-MMM-YYYY') : '')

// An invite's status and expiry, with resend (a NEW key -- the old link stops
// working) and revoke. Both address the invited user row; the key itself is
// never shown or copyable (owner direction 2026-09-27).
export const UserInvite = ({ userLogin, onUpdate }) => {
  const apiFormUserInvite = useApiFormUserInvite()

  const notificationContext = useNotificationContext()

  const status = userLogin.inviteStatus

  const canResend = ['pending', 'expired', 'revoked', 'declined', 'member'].includes(status)
  const canRevoke = status === 'pending' || status === 'member'

  const expiresLabel = useMemo(() => (status === 'expired' ? 'expired' : 'expires'), [status])

  const handleResend = async () => {
    await apiFormUserInvite.put(`user/${userLogin.userID}/resend`)
    notificationContext.toast.notify('Invite email sent', 3000)
    onUpdate && onUpdate()
  }

  // A member whose invitation is still open (their organization's sign-in
  // already made them a member): withdrawing it drops only what it would
  // add; they stay a member (re-review 6 RM6-3).
  const member = status === 'member'

  const handleRevoke = async () => {
    await apiFormUserInvite.put(`user/${userLogin.userID}/revoke`)
    notificationContext.toast.notify(member ? 'Invitation withdrawn' : 'Invite revoked', 3000)
    onUpdate && onUpdate()
  }

  if (!status) {
    return null
  }

  return (
    <Card title="user invite">
      <ItemReadOnly label="status" value={inviteStatusLabel(status)} />
      <ItemReadOnly label="invited" value={day(userLogin.inviteInvitedAt)} />
      {status === 'accepted'
        ? <ItemReadOnly label="accepted" value={day(userLogin.inviteRegisteredAt)} />
        : <ItemReadOnly label={expiresLabel} value={day(userLogin.inviteExpiresAt)} />}

      {(canResend || canRevoke) &&
        <div className="m-2 d-flex gap-2">
          {canResend && <ButtonPrimaryDelay Icon={IconEmail} text="re-send invite email" small onClick={handleResend} />}
          {canRevoke &&
            <ButtonPrimaryConfirm
              Icon={IconDelete}
              text={member ? 'Withdraw invitation (they stay a member)' : 'revoke invite'}
              small
              confirmText={
                member
                  ? "Withdraw this invitation? Its link stops working and what it would add isn't given. They stay a member of this organization. You can send a new one later."
                  : 'Revoke this invitation? Its link stops working. You can send a new one later.'
              }
              onConfirm={handleRevoke}
            />}
        </div>}
    </Card>
  )
}
